import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { firstValueFrom, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  RefusalReason,
  StockyEntryService,
  SubmitReceiptPayload,
} from '../../services/stocky-entry.service';
import { StockEntryState } from '../../store/stock-entry.state';
import { SetLot, SetReceiptFields, StockEntryStateModel } from '../../store/stock-entry.actions';
import {
  DivergenceDialogComponent,
  DivergenceItem,
} from '../../dialogs/divergence-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { TokenService } from '@app/shared/services/token/token.service';
import { AuthState } from '@app/store/auth/auth.state';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { environment } from 'environments/environment';

function imeiValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value as string) ?? '';
  if (!value) return null;
  if (!/^\d{15,16}$/.test(value)) return { imeiLength: true };
  if (value.length === 16) return null;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let d = parseInt(value[i], 10);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0 ? null : { imeiLuhn: true };
}

@Component({
  selector: 'app-create-lot',
  templateUrl: './create-lot.component.html',
  styleUrls: ['./create-lot.component.sass'],
})
export class CreateLotComponent implements OnInit, OnDestroy {
  lot = {
    numero: 0,
    sacaNumero: '',
    quantityChecked: 1,
    quantityToCheck: 0,
    totalQuantity: 1,
    lotDate: '11/08/2026',
  };

  termOptions = ['Sim', 'Não'];
  isValidated = false;
  refusalReasons: RefusalReason[] = [];
  isLoadingRefusalReasons = false;
  hasApiDivergence = false;
  private returnedTradeId = '';
  private returnedInvoiceKey = '';
  private returnedImei = '';
  private returnedNumeroNota: number | null = null;
  private _destroy$ = new Subject<void>();
  @ViewChild('confirmSwitchDialog') confirmSwitchDialog!: TemplateRef<any>;
  activeEvidenciaTab: 'computer' | 'smartphone' = 'computer';
  selectedFiles: File[] = [];
  uploadedUrls: string[] = [];
  mobileUploadUrl = '';
  isGeneratingQr = false;
  elementType = NgxQrcodeElementTypes.URL;
  correctionLevel = NgxQrcodeErrorCorrectionLevels.LOW;

  form = new FormGroup({
    invoiceKey: new FormControl('', [
      Validators.required,
      Validators.maxLength(44),
      Validators.pattern(/^\d+$/),
    ]),
    tradeId: new FormControl('', [
      Validators.required,
      Validators.maxLength(60),
      Validators.pattern(/^[A-Za-z0-9]+$/),
    ]),
    imei: new FormControl('', [Validators.required, imeiValidator]),
    noImei: new FormControl(false),
    receiptRefused: new FormControl(false),
    refusalReasonId: new FormControl({ value: null, disabled: true }, [
      Validators.required,
      (control: AbstractControl): ValidationErrors | null =>
        !control.value || this.refusalReasons.some((reason) => reason.id === control.value)
          ? null
          : { invalidReason: true },
    ]),
    brokenScreen: new FormControl(false),
    brokenCase: new FormControl(false),
    deviceWontTurnOn: new FormControl(false),
    printedTerm: new FormControl('', Validators.required),
  });

  constructor(
    private router: Router,
    private store: Store,
    private stockyEntryService: StockyEntryService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private tokenService: TokenService
  ) {}

  ngOnInit(): void {
    const existingLot = this.store.selectSnapshot(StockEntryState.lot);
    const navigationFields =
      this.router.getCurrentNavigation()?.extras.state?.receiptFields ??
      window.history.state?.receiptFields;
    const hasSavedReceipt = existingLot.receiptTradeId !== null && existingLot.receiptTradeId !== undefined;
    const receiptFields = hasSavedReceipt
      ? {
          tradeId: existingLot.receiptTradeId,
          imei: existingLot.receiptImei,
          invoiceKey: existingLot.receiptInvoiceKey,
        }
      : navigationFields;
    if (receiptFields) {
      this.form.patchValue({
        tradeId: receiptFields.tradeId ?? '',
        imei: receiptFields.imei ?? '',
        invoiceKey: receiptFields.invoiceKey ?? '',
      }, { emitEvent: false });
    }

    this.form.valueChanges.pipe(
      startWith(this.form.getRawValue()),
      map(() => {
        const values = this.form.getRawValue();
        return {
          tradeId: values.tradeId ?? '',
          imei: values.imei ?? '',
          invoiceKey: values.invoiceKey ?? '',
        };
      }),
      distinctUntilChanged((previous, current) =>
        previous.tradeId === current.tradeId &&
        previous.imei === current.imei &&
        previous.invoiceKey === current.invoiceKey
      ),
      takeUntil(this._destroy$)
    ).subscribe(fields => this.store.dispatch(new SetReceiptFields(fields)));

    if (receiptFields) this.validate();

    if (existingLot.lotNumero !== null) {
      this.populateLotFromState(existingLot);
      this.refreshLot(existingLot.lotNumero);
      return;
    }

    this.stockyEntryService
      .createLot()
      .pipe(switchMap((res) => this.stockyEntryService.getLots({ Filtro: String(res.numero) })))
      .subscribe((getResponse) => this.applyLotResponse(getResponse));
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private refreshLot(numero: number): void {
    this.stockyEntryService
      .getLots({ Filtro: String(numero) })
      .subscribe((res) => this.applyLotResponse(res));
  }

  private applyLotResponse(response: any): void {
    const item = response?.page?.[0];
    if (!item) return;
    this.store.dispatch(
      new SetLot({
        id: item.id,
        numero: item.numero,
        sacaNumero: item.sacaNumero,
        status: item.status,
        quantityChecked: item.quantidadeItensConferidos,
        quantityToCheck: item.quantidadeItensAConferir,
        totalQuantity: item.totalItens,
        lotDate: new Date(item.dataLote).toLocaleDateString('pt-BR'),
      })
    );
    this.populateLotFromState(this.store.selectSnapshot(StockEntryState.lot));
  }

  private populateLotFromState(state: StockEntryStateModel): void {
    this.lot.numero = state.lotNumero ?? 0;
    this.lot.sacaNumero = state.lotSacaNumero ?? '';
    this.lot.quantityChecked = state.lotQuantityChecked ?? 0;
    this.lot.quantityToCheck = state.lotQuantityToCheck ?? 0;
    this.lot.totalQuantity = state.lotTotalQuantity ?? 0;
    this.lot.lotDate = state.lotDate ?? '';
  }

  get canValidate(): boolean {
    const noImei = this.form.get('noImei')?.value === true;
    return (
      !!this.form.get('invoiceKey')?.value &&
      !!this.form.get('tradeId')?.value &&
      (noImei || this.form.get('imei')?.valid === true)
    );
  }

  get requiredImages(): number {
    return (this.hasApiDivergence ? 2 : 0) + (this.hasDivergence ? 1 : 0);
  }

  get canSubmit(): boolean {
    if (!this.form.valid) return false;
    if (this.activeEvidenciaTab === 'computer' && this.requiredImages > 0) {
      return this.selectedFiles.length >= this.requiredImages;
    }
    return true;
  }

  get needsMoreFiles(): boolean {
    return this.requiredImages > 0 && this.selectedFiles.length < this.requiredImages;
  }

  get hasDivergence(): boolean {
    return (
      !!this.form.get('brokenScreen')?.value ||
      !!this.form.get('brokenCase')?.value ||
      !!this.form.get('deviceWontTurnOn')?.value
    );
  }

  get isReceiptRefused(): boolean {
    return this.form.get('receiptRefused')?.value === true;
  }

  get selectedRefusalReasonDescription(): string {
    const selectedId = this.form?.get('refusalReasonId')?.value;
    return this.refusalReasons.find((reason) => reason.id === selectedId)?.descricao ?? '';
  }

  onReceiptRefusedChange(checked: boolean): void {
    const control = this.form.get('refusalReasonId');
    if (!checked) {
      control?.reset();
      control?.disable();
      return;
    }

    control?.enable();
    if (this.refusalReasons.length || this.isLoadingRefusalReasons) return;

    this.isLoadingRefusalReasons = true;
    this.stockyEntryService.getRefusalReasons().subscribe({
      next: (reasons) => {
        this.refusalReasons = reasons;
        this.isLoadingRefusalReasons = false;
        control?.updateValueAndValidity();
      },
      error: () => {
        this.isLoadingRefusalReasons = false;
        this.toastr.error('Não foi possível carregar os motivos de recusa. Desmarque e marque novamente para tentar.');
      },
    });
  }

  validate(): void {
    if (!this.canValidate) return;

    const invoiceKey = this.form.get('invoiceKey')?.value ?? '';
    const tradeId = this.form.get('tradeId')?.value ?? '';
    const noImei = this.form.get('noImei')?.value === true;
    const imei = noImei ? '' : this.form.get('imei')?.value ?? '';

    this.form.get('invoiceKey')?.disable();
    this.form.get('tradeId')?.disable();
    this.form.get('imei')?.disable();
    this.form.get('noImei')?.disable();

    const search = noImei ? tradeId : imei;

    this.stockyEntryService.searchTrade(search).pipe(takeUntil(this._destroy$)).subscribe({
      next: (response) => {
        this.returnedTradeId = response.tradeId ?? tradeId;
        this.returnedInvoiceKey = response.noteKey ?? invoiceKey;
        this.returnedImei = response.imei ?? imei;
        this.returnedNumeroNota = response.noteNumber ?? null;

        if (noImei) {
          this.form.get('imei')?.setValue(this.returnedImei, { emitEvent: false });
        }

        // Quando o IMEI não foi informado fisicamente, ele deve ser enviado no
        // recebimento com o valor retornado pelo Trade.
        this.store.dispatch(
          new SetReceiptFields({
            invoiceKey,
            tradeId,
            imei: noImei ? this.returnedImei : imei,
          })
        );

        const noteKeyMatch = invoiceKey === response.noteKey;
        const tradeIdMatch = tradeId === response.tradeId;

        if (noteKeyMatch && tradeIdMatch) {
          this.isValidated = true;
          return;
        }

        const items: DivergenceItem[] = [
          {
            label: 'Chave da nota',
            typed: invoiceKey,
            returned: response.noteKey,
            hasDivergence: !noteKeyMatch,
          },
          {
            label: 'Trade ID',
            typed: tradeId,
            returned: response.tradeId,
            hasDivergence: !tradeIdMatch,
          },
          ...(noImei
            ? [
                {
                  label: 'IMEI',
                  typed: 'Não informado',
                  returned: this.returnedImei,
                  hasDivergence: true,
                },
              ]
            : []),
        ];

        const ref = this.dialog.open(DivergenceDialogComponent, {
          width: '700px',
          disableClose: true,
          data: { items },
        });

        ref.afterClosed().subscribe((confirmed: boolean) => {
          if (!confirmed) {
            this.clearValidation();
            return;
          }
          if (!noteKeyMatch)
            this.form.get('invoiceKey')?.setValue(response.noteKey, { emitEvent: false });
          if (!tradeIdMatch)
            this.form.get('tradeId')?.setValue(response.tradeId, { emitEvent: false });
          this.hasApiDivergence = true;
          this.isValidated = true;
        });
      },
      error: (erro) => {
        const fallbackMessage = noImei
          ? 'Não foi possível localizar um IMEI associado ao Trade informado.'
          : 'Não foi possível validar o IMEI. Por favor, tente novamente.';
        const message = erro?.error?.errors?.[0] ?? erro?.errors?.[0] ?? fallbackMessage;
        this.toastr.error(message);
        this.form.get('invoiceKey')?.enable();
        this.form.get('tradeId')?.enable();
        this.form.get('noImei')?.enable();
        if (!noImei) this.form.get('imei')?.enable();
      },
    });
  }

  private buildPayload(): SubmitReceiptPayload {
    const v = this.form.getRawValue();
    const state = this.store.selectSnapshot(StockEntryState.lot);
    return {
      tradeId: state.receiptTradeId ?? '',
      chaveNota: state.receiptInvoiceKey ?? '',
      imei: state.receiptImei ?? '',
      recusado: !!v.receiptRefused,
      motivoRecusaId: v.receiptRefused ? v.refusalReasonId : null,
      telaQuebrada: !!v.brokenScreen,
      carcacaQuebrada: !!v.brokenCase,
      aparelhoNaoLiga: !!v.deviceWontTurnOn,
      possuiTermoImpresso: v.printedTerm === 'Sim',
      loteId: state.lotId ?? '',
      retornoTradeId: this.returnedTradeId,
      retornoChaveNota: this.returnedInvoiceKey,
      retornoNumeroNota: this.returnedNumeroNota,
      retornoIMEI: this.returnedImei,
      ...(this.uploadedUrls.length > 0 && { imagens: this.uploadedUrls.map((url) => ({ url })) }),
    };
  }

  switchToSmartphoneTab(): void {
    if (this.uploadedUrls.length > 0) {
      const ref = this.dialog.open(this.confirmSwitchDialog, {
        width: '480px',
        disableClose: true,
      });
      ref.afterClosed().subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.stockyEntryService.deleteEvidence(this.uploadedUrls).subscribe();
        this.selectedFiles = [];
        this.uploadedUrls = [];
        this.proceedToSmartphoneTab();
      });
      return;
    }
    this.proceedToSmartphoneTab();
  }

  private proceedToSmartphoneTab(): void {
    this.activeEvidenciaTab = 'smartphone';
    this.mobileUploadUrl = '';
    if (this.isValidated) {
      this.generateQrCode();
    }
  }

  async generateQrCode(): Promise<void> {
    this.isGeneratingQr = true;
    try {
      const token = this.store.selectSnapshot(AuthState).token;
      const tradeId = this.form.getRawValue().tradeId ?? '';
      const result: any = await firstValueFrom(
        this.tokenService.save(token, 'COLLECT_TVF.saveToken')
      );
      const id = result?.Id ?? result?.id;
      const lotNumero = this.store.selectSnapshot(StockEntryState.lot).lotNumero ?? '';
      this.mobileUploadUrl = `${environment.STI_URL}upload/recebimento?t=${tradeId}&i=${id}&lot=${lotNumero}`;
      console.log(this.mobileUploadUrl);
    } catch {
      this.toastr.error('Não foi possível gerar o QR Code. Tente novamente.');
    } finally {
      this.isGeneratingQr = false;
    }
  }

  clearValidation(): void {
    this.isValidated = false;
    this.hasApiDivergence = false;
    this.returnedTradeId = '';
    this.returnedInvoiceKey = '';
    this.returnedImei = '';
    this.returnedNumeroNota = null;
    this.selectedFiles = [];
    this.uploadedUrls = [];
    this.mobileUploadUrl = '';
    this.activeEvidenciaTab = 'computer';
    this.form.reset({
      invoiceKey: '',
      tradeId: '',
      imei: '',
      noImei: false,
      receiptRefused: false,
      refusalReasonId: { value: null, disabled: true },
      brokenScreen: false,
      brokenCase: false,
      deviceWontTurnOn: false,
      printedTerm: '',
    });
    this.form.get('invoiceKey')?.enable();
    this.form.get('tradeId')?.enable();
    this.form.get('imei')?.enable();
    this.form.get('noImei')?.enable();
  }

  onNoImeiChange(checked: boolean): void {
    const imeiControl = this.form.get('imei');
    if (checked) {
      imeiControl?.setValue('');
      imeiControl?.disable();
    } else {
      imeiControl?.enable();
    }
  }

  onNumericInput(field: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/\D/g, '');
    input.value = cleaned;
    this.form.get(field)?.setValue(cleaned);
  }

  onTradeIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    input.value = cleaned;
    this.form.get('tradeId')?.setValue(cleaned);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const tradeId = this.form.getRawValue().tradeId ?? '';
    const newFiles = Array.from(input.files);
    input.value = '';

    newFiles.forEach((file) => {
      this.selectedFiles = [...this.selectedFiles, file];
      this.stockyEntryService.uploadEvidence(tradeId, file).subscribe({
        next: (url) => {
          if (url) {
            this.uploadedUrls = [...this.uploadedUrls, url];
          }
        },
        error: () => {
          this.selectedFiles = this.selectedFiles.filter((f) => f !== file);
        },
      });
    });
  }

  removeFile(index: number): void {
    const url = this.uploadedUrls[index];
    if (url) {
      this.stockyEntryService.deleteEvidence([url]).subscribe();
    }
    this.selectedFiles = this.selectedFiles.filter((_, i) => i !== index);
    this.uploadedUrls = this.uploadedUrls.filter((_, i) => i !== index);
  }

  printTerm(): void {
    const tradeId = this.returnedTradeId || this.form.getRawValue().tradeId;
    if (!tradeId) return;

    this.stockyEntryService.downloadTermo(tradeId).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastr.error('Não foi possível baixar o termo. Tente novamente.');
      },
    });
  }

  cancel(): void {
    this.router.navigate(['/tvf/recebimento/lotes']);
  }

  nextReceiving(): void {
    this.submitReceipt(() => {
      const numero = this.store.selectSnapshot(StockEntryState.lot).lotNumero!;
      this.clearValidation();
      this.refreshLot(numero);
    });
  }

  complete(): void {
    this.submitReceipt(() => {
      this.clearValidation();
      this.router.navigate(['/tvf/recebimento/lotes']);
    });
  }

  private submitReceipt(onSuccess: () => void): void {
    if (!this.canSubmit) {
      this.form.markAllAsTouched();
      return;
    }
    this.stockyEntryService.submitReceipt(this.buildPayload()).subscribe({
      next: () => {
        this.toastr.success('Recebimento registrado com sucesso.');
        onSuccess();
      },
      error: (err) => {
        this.toastr.error(err.errors[0] ?? 'Erro ao registrar o recebimento.');
      },
    });
  }
}
